/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   useNetwork.ts
 *     Feature         :   140-network-topology-dashboard
 *     Description     :   Composite state hook for Network Overview dashboard
 *     Constitution    :   v1.12.0
 *     Author          :   Peter Dobler
 *     Created         :   2026-03-20
 * =========================================================================================
 */

import { useState, useMemo, useCallback } from 'react';
import { useDebounce } from '../../hooks/useDebounce';
import { useNetworkSummary, useVnets } from '../../services/operations/networkService';

export function useNetworkOverview() {
  const [subscription, setSubscription] = useState<string>('');
  const [resourceGroup, setResourceGroup] = useState<string>('');
  const [search, setSearch] = useState<string>('');

  const debouncedSearch = useDebounce(search, 300);

  const filters = useMemo(() => {
    const f: Record<string, unknown> = {};
    if (subscription) f.subscription = subscription;
    if (resourceGroup) f.resourceGroup = resourceGroup;
    if (debouncedSearch) f.search = debouncedSearch;
    return f;
  }, [subscription, resourceGroup, debouncedSearch]);

  const summaryQuery = useNetworkSummary();
  const vnetsQuery = useVnets(filters);

  const handleSubscriptionChange = useCallback((val: string) => { setSubscription(val); }, []);
  const handleResourceGroupChange = useCallback((val: string) => { setResourceGroup(val); }, []);
  const handleSearchChange = useCallback((val: string) => { setSearch(val); }, []);

  const clearFilters = useCallback(() => {
    setSubscription('');
    setResourceGroup('');
    setSearch('');
  }, []);

  return {
    // State
    subscription,
    resourceGroup,
    search,
    // Setters
    setSubscription: handleSubscriptionChange,
    setResourceGroup: handleResourceGroupChange,
    setSearch: handleSearchChange,
    clearFilters,
    // Data
    summary: summaryQuery.data,
    summaryLoading: summaryQuery.isLoading,
    vnets: vnetsQuery.data,
    vnetsLoading: vnetsQuery.isLoading,
    // Refetch
    refetchAll: () => {
      summaryQuery.refetch();
      vnetsQuery.refetch();
    },
  };
}
